import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Crown, Rocket, Star } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const ICONS = { Basic: Star, Pro: Rocket, Enterprise: Crown };

export default function Plans() {
  const [list, setList] = useState([]);
  const [billing, setBilling] = useState("mensile"); // mensile|annuale

  useEffect(() => {
    api.get("/plans").then((r) => setList(r.data));
  }, []);

  const subscribe = async (plan) => {
    const origin = window.location.origin;
    try {
      const r = await api.post("/checkout/session", {
        tipo: "subscription",
        ref_id: plan.id,
        origin_url: origin,
      });
      window.location.href = r.data.url;
    } catch (e) { toast.error("Errore Stripe: " + (e?.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-8" data-testid="plans-page">
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter">
          Piani <span className="neon-text">Subscription</span>
        </h1>
        <p className="text-white/45 mt-3">Per autonoleggi che vogliono crescere senza limiti. Cambia o cancella quando vuoi.</p>
        <div className="mt-6 inline-flex gap-1 p-1 rounded-full bg-white/[0.03] border border-white/10">
          {["mensile", "annuale"].map((b) => (
            <button key={b} onClick={() => setBilling(b)} data-testid={`bill-${b}`}
              className={`px-5 py-2 rounded-full text-xs uppercase tracking-widest transition ${billing === b ? "bg-[#ff0033] text-white" : "text-white/55 hover:text-white"}`}>
              {b} {b === "annuale" && <span className="ml-1 text-[9px] opacity-80">(-17%)</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
        {list.map((p, i) => {
          const Icon = ICONS[p.nome] || Star;
          const featured = p.nome === "Pro";
          const price = billing === "annuale" ? (p.prezzo_annuale / 12).toFixed(0) : p.prezzo_mensile;
          return (
            <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
              className={`relative glass rounded-3xl p-7 ${featured ? "ring-2 ring-[#ff0033] neon-glow" : ""}`} data-testid={`plan-${p.nome}`}>
              {featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#ff0033] text-[10px] uppercase tracking-widest font-bold">Consigliato</div>}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 grid place-items-center rounded-2xl ${featured ? "bg-[#ff0033]/20 border border-[#ff0033]/50" : "bg-white/[0.04] border border-white/10"}`}>
                  <Icon size={20} className={featured ? "text-[#ff0033]" : "text-white/70"} />
                </div>
                <div>
                  <div className="font-display text-2xl font-extrabold">{p.nome}</div>
                  <div className="text-xs text-white/45">{p.descrizione}</div>
                </div>
              </div>
              <div className="mb-6">
                <div className="font-display text-5xl font-black tracking-tighter">
                  €{price}<span className="text-xl text-white/40 font-medium">/mese</span>
                </div>
                {billing === "annuale" && <div className="text-[10px] text-emerald-300 mt-1">€{p.prezzo_annuale} fatturato annuale</div>}
              </div>
              <div className="space-y-2.5 mb-7">
                <Stat label="Veicoli" value={p.max_veicoli >= 9999 ? "Illimitati" : p.max_veicoli} />
                <Stat label="Utenti" value={p.max_utenti >= 9999 ? "Illimitati" : p.max_utenti} />
                <Stat label="Sedi" value={p.max_sedi >= 9999 ? "Illimitate" : p.max_sedi} />
                <div className="h-px bg-white/5 my-3" />
                {p.features.map((f) => (
                  <div key={f} className="flex items-start gap-2 text-sm">
                    <Check size={14} className="text-[#ff0033] mt-0.5 shrink-0" />
                    <span className="text-white/75">{f}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => subscribe(p)} data-testid={`subscribe-${p.nome}`}
                className={`w-full py-3 rounded-full text-sm uppercase tracking-widest font-bold transition ${featured ? "btn-neon" : "bg-white/[0.04] border border-white/10 hover:bg-white/10"}`}>
                Attiva {p.nome}
              </button>
            </motion.div>
          );
        })}
      </div>

      <div className="text-center text-xs text-white/40 max-w-xl mx-auto">
        Pagamenti sicuri con <b className="text-white/60">Stripe</b>. Test mode attivo · Carta di test: <code className="font-mono text-[#ff5577]">4242 4242 4242 4242</code> · qualsiasi data futura / CVC 123.
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-white/45 uppercase tracking-wider">{label}</span>
    <span className="font-display font-bold">{value}</span>
  </div>
);
